import { set } from "@vueuse/core";
import { type } from "arktype";
import { ref, toValue } from "vue";
import { type AddressDetails, ZipcodeResourceSchema } from "../address-auto-complete.types.js";

const baseUrl = "https://florists-new.hijlkemacodes.app/api/florists/v1/zipcode";
const abortController = new AbortController();
const headers = new Headers();
headers.set("Accept", "application/json");

type CallState = "loading" | "error" | "done" | "default";

export function useZipcodeValidation() {
	const status = ref<CallState>("default");
	const isValid = ref<boolean | undefined>(undefined);

	/**
	 * @todo doc
	 */
	async function validate(zipcode: string) {
		if (toValue(status) === "loading") {
			abortController.abort();
		}

		// if the zipcode is known invalid (short) do not make API call.
		if (zipcode.length < 6) {
			set(isValid, false);
			return;
		}

		const url = new URL(`${baseUrl}/validate`);

		url.searchParams.set("zipcode", zipcode);

		let res: Response;
		try {
			res = await fetch(url);
		} catch {
			set(status, "error");
			set(isValid, false);

			return;
		}

		if (res.ok) {
			set(status, "done");
			set(isValid, true);
		} else {
			set(status, "error");
			set(isValid, false);
		}
	}

	return { validate, isValid, status };
}

export function useZipcodeDetails() {
	const status = ref<CallState>("default");
	const addressDetails = ref<AddressDetails | undefined>(undefined);

	async function getData(zipcode: string, house_number: number, extension?: string) {
		if (toValue(status) === "loading") {
			abortController.abort();
		}

		const url = new URL(`${baseUrl}/address-details`);
		url.searchParams.set("zipcode", zipcode);
		url.searchParams.set("number", `${house_number}`);
		if (extension) {
			url.searchParams.set("extension", extension);
		}

		let res: Response;
		try {
			res = await fetch(url);
		} catch {
			set(status, "error");
			return;
		}

		if (!res.ok) {
			set(status, "error");
			set(addressDetails, undefined);
			return;
		}

		// transform the data into AddressDetails,
		const arkBody = ZipcodeResourceSchema(await res.json());

		if (arkBody instanceof type.errors) {
			set(status, "error");
			return;
		}

		set(status, "done");
		set(addressDetails, {
			country: arkBody.data.country,
			city: arkBody.data.city,
			street: arkBody.data.street,
			postcode: arkBody.data.postcode,
			house_number: arkBody.data.house_number,
			extension: arkBody.data.extension ?? null,
			municipality: arkBody.data.municipality,
			province: arkBody.data.province,
		});
	}

	return { status, addressDetails, getData };
}
